import React, { Fragment } from 'react'
import { AiOutlineEye } from 'react-icons/ai'
import { BsCodeSlash } from 'react-icons/bs'
import OnboardingPopup, {
  OnboardingPopupImg,
  OnboardingPopupList,
  OnboardingPopupListItem,
} from '@components/OnboardingPopup/OnboardingPopup'
import Box from '@ui/Box'
import { AnalyticsEvents, sendAnalyticsEvent } from '@utils/analytics'

type Props = {
  open: boolean
  onClose: () => void
  demo?: boolean
}

const MainOnboarding = ({ open, onClose, demo }: Props) => {
  return (
    <>
      <OnboardingPopup
        onClose={onClose}
        open={open}
        onSubmit={() => {
          sendAnalyticsEvent(AnalyticsEvents.onboardingCompleted)
          onClose()
        }}
        title="Welcome to Enhanci! 🎉"
        preload={[
          '/img/onboarding/step-1.gif',
          '/img/onboarding/step-3.gif',
          '/img/onboarding/step-4.gif',
          '/img/onboarding/step-5.gif',
          '/img/onboarding/step-6.gif',
          '/img/onboarding/step-7.gif',
        ]}
        steps={[
          <Fragment key={1}>
            We're thrilled to have you join our platform! Let's make creating pricing
            plans for your website a piece of cake. Here's how it all comes together:
            <OnboardingPopupList>
              {demo ? (
                <OnboardingPopupListItem>
                  🌟 <strong>Step 1: Your widget is created!</strong> <br />
                </OnboardingPopupListItem>
              ) : (
                <OnboardingPopupListItem>
                  🌟 <strong>Step 1: Create your widget</strong> <br />
                  Click the "Create Now" button to start the fun. Your very first widget
                  is just a click away – it's that simple!
                </OnboardingPopupListItem>
              )}

              <OnboardingPopupListItem>
                🎨 <strong>Step 2: Choose your design</strong> <br />
                Go to the Templates menu and pick a design that speaks to you. Your
                pricing plans will look fabulous in no time.
              </OnboardingPopupListItem>
            </OnboardingPopupList>
            <OnboardingPopupImg src="/img/onboarding/step-1.gif" />
          </Fragment>,
          <Fragment key={2}>
            <OnboardingPopupList>
              <OnboardingPopupListItem>
                📝 <strong>Step 3: Make it yours</strong> <br />
                The Cards menu is where the real magic happens. Add, remove, and edit
                pricing cards just the way you want. Change plan names, prices,
                descriptions, and more – you're in control!
              </OnboardingPopupListItem>
            </OnboardingPopupList>
            <OnboardingPopupImg src="/img/onboarding/step-3.gif" />
          </Fragment>,
          <Fragment key={3}>
            <OnboardingPopupList>
              <OnboardingPopupListItem>
                🎈 <strong>Step 4: Add your touch</strong> <br />
                Customize things like colors, fonts, and backgrounds to match your brand.
                Your widget will look fantastic and totally 'you.'
              </OnboardingPopupListItem>
            </OnboardingPopupList>
            <OnboardingPopupImg src="/img/onboarding/step-4.gif" />
          </Fragment>,
          <Fragment key={4}>
            <OnboardingPopupList>
              <OnboardingPopupListItem>
                ⏳ <strong>Step 5: Adjust the settings</strong> <br />
                In the Settings menu, decide if you want monthly or yearly subscription
                options – you call the shots!
              </OnboardingPopupListItem>
            </OnboardingPopupList>
            <OnboardingPopupImg src="/img/onboarding/step-5.gif" />
          </Fragment>,
          <Fragment key={5}>
            <OnboardingPopupList>
              <OnboardingPopupListItem>
                🗂 <strong>Step 6: Add compare plans table and FAQ section</strong> <br />
                You will find more instructions in "Compare plans" and "FAQ" menu options.
              </OnboardingPopupListItem>
            </OnboardingPopupList>
            <OnboardingPopupImg src="/img/onboarding/step-6.gif" />
          </Fragment>,
          <Fragment key={7}>
            {demo ? (
              <>
                <OnboardingPopupList>
                  <OnboardingPopupListItem>
                    👀 <strong>Step 7: Signup to publish your widget</strong>
                    <br />
                    Click Save to signup. You'll be redirected to the signup page. Once
                    you signup, you'll be able to preview and publish your widget
                  </OnboardingPopupListItem>
                </OnboardingPopupList>
                <OnboardingPopupImg src="/img/onboarding/step-7-demo.gif" />
              </>
            ) : (
              <>
                <OnboardingPopupList>
                  <OnboardingPopupListItem>
                    👀 <strong>Step 7: See the magic</strong>
                    <br />
                    Curious how your masterpiece looks? Click the <AiOutlineEye /> icon
                    and be prepared to be wowed.
                  </OnboardingPopupListItem>
                  <OnboardingPopupListItem>
                    🚀 <strong>Step 8: Share it with the world</strong>
                    <br />
                    When you're ready, hit the Publish button. Your widget is now ready
                    for the big stage – your website!
                  </OnboardingPopupListItem>
                  <OnboardingPopupListItem>
                    🔍 <strong>Step 9: Easy Installation</strong>
                    <br />
                    Want to know how to put your widget on your website? Click the{' '}
                    <BsCodeSlash /> icon, and we'll guide you step by step.
                  </OnboardingPopupListItem>
                </OnboardingPopupList>
                <OnboardingPopupImg src="/img/onboarding/step-7.gif" />
              </>
            )}
          </Fragment>,
          <Fragment key={6}>
            <Box>
              We're here with you every step of the way. If you need help or some
              inspiration, just drop us an email at{' '}
              <a href="mailto:inbox@enhanci.com">inbox@enhanci.com</a>. Let's dive into
              Enhanci and create pricing plan magic that'll impress your website visitors!
            </Box>
          </Fragment>,
        ]}
      />
    </>
  )
}

export default MainOnboarding
